/**
 * screens/hooks/useNowServing.ts
 * Live data for the public Now Serving door board. Uses the Backbone-minted
 * guest token (via guestSession.authHeader) and polls getNowServing — there is
 * no WS in queue-api, and a door TV refreshing every few seconds is the board.
 * Pass null queueId/authHeader to disable (in-memory demo).
 */

import { useEffect, useState, useCallback } from 'react';
import { getNowServing } from '../../services/queue/queueService';
import { handleApiError } from '../../utils/errorHandler';
import type { NowServingView } from '../../api/queueDtos';

const POLL_MS = 4000;

export function useNowServing(
  queueId: string | null,
  authHeader: () => Promise<Record<string, string> | null>,
): { view: NowServingView | null } {
  const [view, setView] = useState<NowServingView | null>(null);

  const fetchOnce = useCallback(async (id: string) => {
    try {
      const headers = await authHeader();
      if (!headers) return;
      const v = await getNowServing(id, { headers });
      setView(v);
    } catch (err) {
      handleApiError(err, 'Could not load the board');
    }
  }, [authHeader]);

  useEffect(() => {
    if (!queueId) { setView(null); return; }
    fetchOnce(queueId);
    const t = setInterval(() => fetchOnce(queueId), POLL_MS);
    return () => clearInterval(t);
  }, [queueId, fetchOnce]);

  return { view };
}
